<!-- src/components/layouts/EmployeeLayout.vue -->
<template>
  <div class="dark employee-portal min-h-screen relative isolate overflow-x-hidden"
       style="background-color: #060a18">

    <!-- ── Background ─────────────────────────────────────────── -->
    <div class="fixed inset-0 -z-10"
         style="background: radial-gradient(ellipse at 15% 40%, rgba(99,102,241,0.06) 0%, transparent 55%),
                            radial-gradient(ellipse at 85% 15%, rgba(139,92,246,0.04) 0%, transparent 50%),
                            linear-gradient(160deg, #060a18 0%, #080d1e 60%, #050812 100%)">
    </div>

    <!-- ── Desktop Sidebar ────────────────────────────────────── -->
    <aside class="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 z-40"
           style="background: rgba(6,10,24,0.88); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
                  border-right: 1px solid rgba(255,255,255,0.06);">

      <!-- Logo -->
      <div class="flex items-center gap-3 px-5 h-16 shrink-0"
           style="border-bottom: 1px solid rgba(255,255,255,0.06)">
        <img :src="logoMark" alt="ExoTrack" class="w-9 h-9 rounded-xl"
             style="box-shadow: 0 0 16px rgba(108,92,231,0.4)" />
        <div>
          <p class="text-sm font-bold font-display text-white leading-tight">ExoTrack</p>
          <p class="text-[10px] text-indigo-400 font-medium leading-tight">{{ t('nav.employeePortal') }}</p>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-hide">
        <router-link
          v-for="item in navigation"
          :key="item.name"
          :to="item.href"
          :class="[
            'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group relative',
            isRouteActive(item.href)
              ? 'text-indigo-300 bg-indigo-500/10'
              : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
          ]"
        >
          <!-- Active pill -->
          <div v-if="isRouteActive(item.href)"
               class="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-indigo-500 rounded-r-full" />
          <component :is="item.icon"
            :class="['w-4 h-4 shrink-0 transition-colors', isRouteActive(item.href) ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300']"
          />
          <span class="flex-1">{{ item.label }}</span>
          <span v-if="getNavBadge(item.name)"
                class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-indigo-500 text-white rounded-full">
            {{ getNavBadge(item.name) }}
          </span>
        </router-link>
      </nav>

      <!-- Bottom section -->
      <div class="px-3 pb-4 space-y-2 shrink-0" style="border-top: 1px solid rgba(255,255,255,0.06); padding-top: 12px;">
        <!-- Clock status -->
        <button @click="router.push('/employee/attendance')"
                class="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-all hover:bg-white/5"
                style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06)">
          <div :class="['w-2 h-2 rounded-full shrink-0', attendanceStore.isActive ? 'bg-green-400 animate-pulse' : 'bg-slate-600']" />
          <div class="flex-1 min-w-0">
            <p class="text-xs font-medium" :class="attendanceStore.isActive ? 'text-green-400' : 'text-slate-500'">
              {{ attendanceStore.isActive ? t('attendance.clockedIn') : t('attendance.clockedOut') }}
            </p>
            <p v-if="attendanceStore.isActive" class="text-[10px] text-slate-500 truncate">
              {{ attendanceStore.currentWorkDuration }}
            </p>
          </div>
          <Clock class="w-3.5 h-3.5 text-slate-600 shrink-0" />
        </button>

        <!-- User row -->
        <div class="flex items-center gap-2.5 px-2 py-1.5">
          <div class="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
               style="background: rgba(99,102,241,0.15); border: 1px solid rgba(99,102,241,0.25)">
            <User class="w-4 h-4 text-indigo-400" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-xs font-semibold text-slate-200 truncate">{{ currentUser.name }}</p>
            <p class="text-[10px] text-slate-500 capitalize">{{ currentUser.role }}</p>
          </div>
          <button @click="confirmLogout"
                  class="p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-all"
                  title="Sign out">
            <LogOut class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </aside>

    <!-- ── Mobile Top Bar ─────────────────────────────────────── -->
    <header class="lg:hidden fixed top-0 left-0 right-0 z-40 h-14 flex items-center justify-between px-4"
            style="background: rgba(6,10,24,0.92); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
                   border-bottom: 1px solid rgba(255,255,255,0.06)">
      <!-- Logo -->
      <div class="flex items-center gap-2.5">
        <img :src="logoMark" alt="ExoTrack" class="w-7 h-7 rounded-lg"
             style="box-shadow: 0 0 12px rgba(108,92,231,0.4)" />
        <span class="text-sm font-bold font-display text-white">ExoTrack</span>
      </div>

      <!-- Right controls -->
      <div class="flex items-center gap-1.5">
        <!-- Language toggle -->
        <LangToggle variant="dark" />
        <!-- Clock pill -->
        <div class="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
             style="background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08)">
          <div :class="['w-1.5 h-1.5 rounded-full', attendanceStore.isActive ? 'bg-green-400 animate-pulse' : 'bg-slate-600']" />
          <span :class="attendanceStore.isActive ? 'text-green-400' : 'text-slate-500'">
            {{ attendanceStore.isActive ? t('attendance.clockIn') : t('attendance.clockOut') }}
          </span>
        </div>

        <!-- Notifications -->
        <div class="relative">
          <button @click.stop="showNotifications = !showNotifications"
                  class="relative p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-all">
            <Bell class="w-4.5 h-4.5" />
            <span v-if="unreadNotifications > 0"
                  class="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 flex items-center justify-center text-[9px] font-bold bg-indigo-500 text-white rounded-full">
              {{ unreadNotifications > 9 ? '9+' : unreadNotifications }}
            </span>
          </button>

          <!-- Notification dropdown -->
          <transition enter-active-class="transition-all duration-200 ease-out"
                      enter-from-class="opacity-0 scale-95 -translate-y-2"
                      enter-to-class="opacity-100 scale-100 translate-y-0"
                      leave-active-class="transition-all duration-150 ease-in"
                      leave-from-class="opacity-100 scale-100 translate-y-0"
                      leave-to-class="opacity-0 scale-95 -translate-y-2">
            <div v-if="showNotifications"
                 class="absolute right-0 top-full mt-2 w-80 rounded-2xl overflow-hidden z-50 shadow-2xl"
                 style="background: rgba(10,14,28,0.95); backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.08)"
                 @click.stop>
              <div class="flex items-center justify-between px-4 py-3" style="border-bottom: 1px solid rgba(255,255,255,0.06)">
                <p class="text-sm font-semibold text-slate-200">{{ t('notifications.title') }}</p>
                <button @click="markAllAsRead" class="text-xs text-indigo-400 hover:text-indigo-300 transition-colors">
                  {{ t('notifications.markAllRead') }}
                </button>
              </div>
              <div class="max-h-72 overflow-y-auto scrollbar-hide">
                <div v-for="n in notifications.slice(0,5)" :key="n.id"
                     @click="handleNotificationClick(n)"
                     :class="['flex items-start gap-3 px-4 py-3 cursor-pointer transition-all',
                              n.read ? 'hover:bg-white/3' : 'bg-indigo-500/5 hover:bg-indigo-500/8 border-l-2 border-indigo-500']"
                     style="border-bottom: 1px solid rgba(255,255,255,0.04)">
                  <div :class="['w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5', getNotifBg(n.type)]">
                    <component :is="getNotifIcon(n.type)" class="w-3.5 h-3.5 text-white" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-medium text-slate-200 truncate">{{ n.title }}</p>
                    <p class="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{{ n.message }}</p>
                    <p class="text-[10px] text-slate-600 mt-1">{{ formatNotifTime(n.timestamp) }}</p>
                  </div>
                  <div v-if="!n.read" class="w-1.5 h-1.5 bg-indigo-400 rounded-full mt-1.5 shrink-0" />
                </div>
              </div>
              <div class="px-4 py-2.5" style="border-top: 1px solid rgba(255,255,255,0.06)">
                <button class="w-full text-xs text-indigo-400 hover:text-indigo-300 text-center py-1 transition-colors">
                  {{ t('notifications.viewAll') }}
                </button>
              </div>
            </div>
          </transition>
        </div>

        <!-- Avatar -->
        <div class="relative" ref="profileDropdown">
          <button @click="showProfileMenu = !showProfileMenu"
                  class="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:ring-2 hover:ring-indigo-500/50"
                  style="background: rgba(99,102,241,0.15); border: 1px solid rgba(99,102,241,0.25)">
            <User class="w-4 h-4 text-indigo-400" />
          </button>

          <!-- Profile dropdown -->
          <transition enter-active-class="transition-all duration-200 ease-out"
                      enter-from-class="opacity-0 scale-95 -translate-y-2"
                      enter-to-class="opacity-100 scale-100 translate-y-0"
                      leave-active-class="transition-all duration-150 ease-in"
                      leave-from-class="opacity-100 scale-100 translate-y-0"
                      leave-to-class="opacity-0 scale-95 -translate-y-2">
            <div v-if="showProfileMenu"
                 class="absolute right-0 top-full mt-2 w-56 rounded-2xl overflow-hidden z-50 shadow-2xl"
                 style="background: rgba(10,14,28,0.95); backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.08)"
                 @click.stop>
              <!-- User info header -->
              <div class="px-4 py-3" style="border-bottom: 1px solid rgba(255,255,255,0.06)">
                <p class="text-sm font-semibold text-slate-200 truncate">{{ currentUser.name }}</p>
                <p class="text-xs text-slate-500 truncate">{{ currentUser.email }}</p>
                <p class="text-[10px] text-indigo-400 mt-0.5 capitalize">{{ currentUser.role }}</p>
              </div>
              <div class="py-1">
                <router-link to="/employee/profile" @click="showProfileMenu = false"
                             class="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-all">
                  <UserCircle class="w-4 h-4" />
                  {{ t('nav.profileSettings') }}
                </router-link>
                <router-link to="/employee/attendance" @click="showProfileMenu = false"
                             class="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-all">
                  <Clock class="w-4 h-4" />
                  {{ t('nav.attendance') }}
                </router-link>
                <router-link to="/employee/user-manual" @click="showProfileMenu = false"
                             class="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-all">
                  <BookOpen class="w-4 h-4" />
                  {{ t('nav.userManual') }}
                </router-link>
                <router-link to="/employee/get-the-app" @click="showProfileMenu = false"
                             class="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-all">
                  <Smartphone class="w-4 h-4" />
                  {{ t('nav.getTheApp') }}
                </router-link>
              </div>
              <div style="border-top: 1px solid rgba(255,255,255,0.06)">
                <button @click="confirmLogout"
                        class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/8 transition-all">
                  <LogOut class="w-4 h-4" />
                  {{ t('auth.signOut') }}
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </header>

    <!-- ── Page Content ───────────────────────────────────────── -->
    <main class="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 min-h-screen">
      <router-view />
    </main>

    <!-- ── Mobile Bottom Tab Bar ─────────────────────────────── -->
    <nav class="lg:hidden fixed bottom-0 left-0 right-0 z-40"
         style="background: rgba(6,10,24,0.95); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
                border-top: 1px solid rgba(255,255,255,0.07)">
      <div class="flex items-stretch">
        <router-link
          v-for="item in mobileNavigation"
          :key="item.name"
          :to="item.href"
          :class="[
            'flex-1 flex flex-col items-center justify-center gap-1 py-2.5 px-1 relative transition-all duration-150 min-h-[56px]',
            isRouteActive(item.href) ? 'text-indigo-400' : 'text-slate-600 active:text-slate-400'
          ]"
        >
          <!-- Top indicator -->
          <div v-if="isRouteActive(item.href)"
               class="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[2px] rounded-full bg-indigo-500" />
          <!-- Icon -->
          <div class="relative">
            <component :is="item.icon" class="w-5 h-5" />
            <span v-if="getNavBadge(item.name)"
                  class="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 px-0.5 flex items-center justify-center text-[8px] font-bold bg-indigo-500 text-white rounded-full">
              {{ getNavBadge(item.name) }}
            </span>
          </div>
          <span class="text-[10px] font-medium leading-none">{{ item.shortLabel }}</span>
        </router-link>
      </div>
    </nav>

    <!-- ── Logout Confirmation ────────────────────────────────── -->
    <transition enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-all duration-150 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0">
      <div v-if="showConfirmLogout"
           class="fixed inset-0 z-50 flex items-center justify-center p-4"
           style="background: rgba(0,0,0,0.6); backdrop-filter: blur(4px)"
           @click.self="showConfirmLogout = false">
        <div class="w-full max-w-sm rounded-2xl p-6 shadow-2xl"
             style="background: rgba(10,14,28,0.98); border: 1px solid rgba(255,255,255,0.1)">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-red-500/15 rounded-xl flex items-center justify-center">
              <LogOut class="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p class="text-sm font-semibold text-slate-200">{{ t('signOut.title') }}</p>
              <p class="text-xs text-slate-500">{{ t('signOut.message') }}</p>
            </div>
          </div>
          <p class="text-xs text-slate-400 mb-5">{{ t('signOut.message') }}</p>
          <div class="flex gap-2">
            <button @click="showConfirmLogout = false"
                    class="flex-1 py-2.5 rounded-xl text-sm font-medium text-slate-300 transition-all hover:bg-white/5"
                    style="border: 1px solid rgba(255,255,255,0.08)">
              {{ t('signOut.cancel') }}
            </button>
            <button @click="logout"
                    class="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-all">
              {{ t('signOut.confirm') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── Toast Notifications ────────────────────────────────── -->
    <transition-group name="toast" tag="div"
                      class="fixed bottom-24 lg:bottom-4 right-4 z-50 space-y-2 max-w-xs w-full pointer-events-none">
      <div v-for="toast in toasts" :key="toast.id"
           class="pointer-events-auto rounded-xl px-4 py-3 shadow-2xl flex items-center gap-3"
           style="background: rgba(10,14,28,0.95); backdrop-filter: blur(12px); border: 1px solid rgba(255,255,255,0.08)">
        <component :is="getToastIcon(toast.type)"
                   :class="['w-4 h-4 shrink-0', getToastColor(toast.type)]" />
        <div class="flex-1 min-w-0">
          <p class="text-xs font-medium text-slate-200">{{ toast.title }}</p>
          <p v-if="toast.message" class="text-[11px] text-slate-500 mt-0.5">{{ toast.message }}</p>
        </div>
        <button @click="removeToast(toast.id)" class="text-slate-600 hover:text-slate-400 shrink-0">
          <X class="w-3.5 h-3.5" />
        </button>
      </div>
    </transition-group>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useAttendanceStore } from '@/stores/attendance';
import employeeService from '@/services/employeeService';
import LangToggle from '@/components/common/LangToggle.vue';
import logoMark from '@/assets/brand/logo-mark.svg';
import {
  Bell, User, UserCircle, Clock, LogOut, BookOpen, Smartphone,
  Home, BarChart3, Calendar, FileText, CreditCard, History, ScanFace,
  X, CheckCircle, AlertTriangle, XCircle, Info,
} from 'lucide-vue-next';

const { t } = useI18n();

const route  = useRoute();
const router = useRouter();
const auth   = useAuthStore();
const attendanceStore = useAttendanceStore();

const profileDropdown    = ref(null);
const showProfileMenu    = ref(false);
const showNotifications  = ref(false);
const showConfirmLogout  = ref(false);
const toasts             = ref([]);
const unreadNotifications = ref(0);

// ── Current user ────────────────────────────────────────────────
const currentUser = computed(() => ({
  name:       auth.userName || '',
  email:      auth.userEmail || '',
  employeeId: auth.user?.id || '',
  role:       auth.userRole || 'employee',
}));

// ── Navigation ──────────────────────────────────────────────────
const navigation = computed(() => [
  { name: 'dashboard',      label: t('nav.dashboard'),      href: '/employee/dashboard',      icon: BarChart3   },
  { name: 'attendance',     label: t('nav.attendance'),     href: '/employee/attendance',     icon: Clock       },
  { name: 'history',        label: t('nav.history'),        href: '/employee/history',        icon: History     },
  { name: 'leaveRequests',  label: t('nav.leaveRequests'),  href: '/employee/leave-requests', icon: FileText    },
  { name: 'payslips',       label: t('nav.payslips'),       href: '/employee/payslips',       icon: CreditCard  },
  { name: 'profile',        label: t('nav.profile'),        href: '/employee/profile',        icon: User        },
  { name: 'faceEnrollment', label: t('nav.faceEnrollment'), href: '/employee/face-enrollment', icon: ScanFace    },
  { name: 'getTheApp',      label: t('nav.getTheApp'),      href: '/employee/get-the-app',    icon: Smartphone  },
]);

const mobileNavigation = computed(() => [
  { name: 'dashboard',     shortLabel: t('nav.home'),      href: '/employee/dashboard',      icon: Home      },
  { name: 'attendance',    shortLabel: t('nav.clock'),     href: '/employee/attendance',     icon: Clock     },
  { name: 'history',       shortLabel: t('nav.history'),   href: '/employee/history',        icon: History   },
  { name: 'leaveRequests', shortLabel: t('nav.leave'),     href: '/employee/leave-requests', icon: Calendar  },
  { name: 'payslips',      shortLabel: t('nav.pay'),       href: '/employee/payslips',       icon: CreditCard},
]);

const isRouteActive = (href) => route.path === href || route.path.startsWith(href + '/');

const getNavBadge = (name) => null;

// ── Notifications ───────────────────────────────────────────────
const notifications = ref([]);
let notifPollTimer = null;

// Map backend notification types onto the dropdown's visual styles
const notifStyleFor = (type) => {
  if (/approved|activated|payslip/.test(type)) return 'success';
  if (/rejected|expired|force_closed/.test(type)) return 'error';
  if (/clock_out|review|warning|debt/.test(type)) return 'warning';
  return 'info';
};

const fetchNotifications = async () => {
  try {
    const res = await employeeService.getNotifications({ limit: 20 });
    const data = res?.data || {};
    notifications.value = (data.notifications || []).map(n => ({
      id: n.id,
      type: notifStyleFor(n.type || ''),
      title: n.title,
      message: n.message,
      timestamp: n.createdAt,
      read: n.isRead,
    }));
    unreadNotifications.value = data.unreadCount || 0;
  } catch (_) { /* keep last known state */ }
};

const getNotifIcon = (type) => ({ success: CheckCircle, warning: AlertTriangle, error: XCircle })[type] ?? Info;
const getNotifBg   = (type) => ({ success: 'bg-green-500/70', warning: 'bg-yellow-500/70', error: 'bg-red-500/70', info: 'bg-indigo-500/70' })[type] ?? 'bg-indigo-500/70';

const formatNotifTime = (ts) => {
  const d = Date.now() - new Date(ts);
  if (d < 60000)    return 'Just now';
  if (d < 3600000)  return `${Math.floor(d/60000)}m ago`;
  if (d < 86400000) return `${Math.floor(d/3600000)}h ago`;
  return `${Math.floor(d/86400000)}d ago`;
};

const handleNotificationClick = (n) => {
  if (!n.read) {
    n.read = true;
    unreadNotifications.value = Math.max(0, unreadNotifications.value - 1);
    employeeService.markNotificationAsRead(n.id).catch(() => {});
  }
  showNotifications.value = false;
};

const markAllAsRead = () => {
  notifications.value.forEach(n => { n.read = true; });
  unreadNotifications.value = 0;
  employeeService.markAllNotificationsAsRead().catch(() => {});
};

// ── Auth ─────────────────────────────────────────────────────────
const confirmLogout = () => { showProfileMenu.value = false; showConfirmLogout.value = true; };

const logout = async () => {
  showConfirmLogout.value = false;
  try {
    await auth.logout();
  } catch (_) { /* proceed anyway */ }
  window.location.href = '/login';
};

// ── Toast system ────────────────────────────────────────────────
const showToast = (type, message, title = null) => {
  const labels = { success: 'Success', error: 'Error', warning: 'Warning', info: 'Info' };
  const toast = { id: Date.now(), type, title: title ?? labels[type] ?? 'Info', message };
  toasts.value.push(toast);
  setTimeout(() => removeToast(toast.id), 5000);
};
const removeToast = (id) => { const i = toasts.value.findIndex(t => t.id === id); if (i > -1) toasts.value.splice(i, 1); };
const getToastIcon  = (type) => ({ success: CheckCircle, warning: AlertTriangle, error: XCircle })[type] ?? Info;
const getToastColor = (type) => ({ success: 'text-green-400', warning: 'text-yellow-400', error: 'text-red-400', info: 'text-indigo-400' })[type] ?? 'text-indigo-400';

// ── Click outside ───────────────────────────────────────────────
const handleClickOutside = (e) => {
  if (profileDropdown.value && !profileDropdown.value.contains(e.target)) showProfileMenu.value = false;
  if (!e.target.closest('[data-notif]')) showNotifications.value = false;
};

// ── Online status ───────────────────────────────────────────────
const handleOnline  = () => showToast('success', 'Connection restored');
const handleOffline = () => showToast('warning', 'You are offline');

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('online',  handleOnline);
  window.addEventListener('offline', handleOffline);
  if (attendanceStore?.initialize) attendanceStore.initialize().catch(() => {});
  fetchNotifications();
  notifPollTimer = setInterval(fetchNotifications, 60 * 1000);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('online',  handleOnline);
  window.removeEventListener('offline', handleOffline);
  if (notifPollTimer) clearInterval(notifPollTimer);
});
</script>

<style scoped>
.toast-enter-active  { transition: all 0.25s ease-out; }
.toast-leave-active  { transition: all 0.2s ease-in; }
.toast-enter-from    { transform: translateX(100%); opacity: 0; }
.toast-leave-to      { transform: translateX(100%); opacity: 0; }

/* Tiny scrollbar for sidebar nav */
nav::-webkit-scrollbar       { width: 3px; }
nav::-webkit-scrollbar-track { background: transparent; }
nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 99px; }
</style>
